import React, { useMemo, useEffect, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import MetricTrend from './components/MetricTrend';
import MetricRetry from './components/MetricRetry';
import Header from './Header';
import { MetricPanelWrapper, OverviewWrapper, Panel, BodySection } from './styled';
import ErrorBoundary from '@src/components/common/Retry/ErrorBoundary';
import useFetchMetrics from '../hooks/useFetchMetrics';
import { enableDodAtom, selectedMetricsAtom, panelsAtom, metricUnitAtom } from '../atoms';
import { getPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';

// Helper function to calculate percentage change
const calculatePercentageChange = (current: number, previous: number) => {
  if (!previous || previous === 0) return null;
  return ((current - previous) / previous) * 100;
};

const MetricPanel = () => {
  const enableDod = useAtomValue(enableDodAtom);
  const [panels] = useAtom(panelsAtom);
  const [selectedMetrics] = useAtom(selectedMetricsAtom);
  const unit = useAtomValue(metricUnitAtom);

  // Global retry state - shared across all metrics
  const [globalRetryAttempts, setGlobalRetryAttempts] = useState(0);
  const MAX_GLOBAL_RETRY_ATTEMPTS = 3;
  const globalRetryExceeded = globalRetryAttempts >= MAX_GLOBAL_RETRY_ATTEMPTS;

  const handleGlobalRetry = () => {
    if (globalRetryAttempts < MAX_GLOBAL_RETRY_ATTEMPTS) {
      setGlobalRetryAttempts((prev) => prev + 1);
      refetch();
    }
  };

  // Compute the metrics to fetch - always fetches BOTH count and percentage versions
  // for performance optimization (pre-fetching). The 'unit' toggle only affects display,
  // not what data is fetched. This is why 'unit' is intentionally omitted from dependencies.
  const metricsToFetch = useMemo(() => {
    const metrics: string[] = [];
    selectedMetrics.map((m) => {
      metrics.push(m);
      const pctKey = getPercentageMetricByKey({ metricKey: m });
      if (pctKey) {
        metrics.push(pctKey);
      }
    });
    return [...new Set(metrics)].sort();
  }, [selectedMetrics]);

  const { isFetching, isError, data, dod, refetch } = useFetchMetrics({
    metrics: metricsToFetch,
    panelEnabled: panels.audience,
    enableDoD: enableDod,
  });

  // Calculate percentage changes for each metric (only when DOD is enabled)
  const metricChanges = useMemo(() => {
    if (!enableDod || !dod?.data?.totals || !data?.totals) return {};

    const changes: Record<string, number | null> = {};
    metricsToFetch.forEach((metricKey) => {
      const currentTotal = data.totals[metricKey] || 0;
      const previousTotal = dod.data.totals[metricKey] || 0;
      changes[metricKey] = calculatePercentageChange(currentTotal, previousTotal);
    });

    return changes;
  }, [enableDod, data?.totals, dod?.data?.totals, metricsToFetch]);

  const isLoading = isFetching || (enableDod && (dod?.isFetching ?? false));

  // Reset global retry attempts when data loads successfully
  useEffect(() => {
    if (!isError && !isLoading && globalRetryAttempts > 0) {
      setGlobalRetryAttempts(0);
    }
  }, [isError, isLoading, globalRetryAttempts]);

  return (
    <ErrorBoundary>
      <MetricPanelWrapper style={{ display: panels.audience ? 'block' : 'none' }}>
        <OverviewWrapper>
          <Header metricsToFetch={metricsToFetch} />
          <BodySection>
            <Panel>
              {selectedMetrics.map((metricKey) => {
                if (unit === '%') {
                  metricKey = getPercentageMetricByKey({ metricKey, defaultValue: metricKey });
                }
                const hasData = data?.data?.[metricKey]?.length > 0;
                const total = data?.totals?.[metricKey];
                const hasTotal = total !== null && total !== undefined;
                return (
                  <MetricRetry
                    key={`${metricKey}-${unit}`}
                    metricKey={metricKey}
                    isError={isError}
                    isLoading={isLoading}
                    onRetry={handleGlobalRetry}
                    globalRetryAttempts={globalRetryAttempts}
                    globalRetryExceeded={globalRetryExceeded}
                  >
                    <MetricTrend
                      key={`${metricKey}-${unit}`}
                      metricKey={metricKey}
                      data={data?.data?.[metricKey] || []}
                      dodData={enableDod ? dod?.data?.data?.[metricKey] || [] : []}
                      total={hasTotal ? total : null}
                      percentageChange={enableDod ? metricChanges[metricKey] : null}
                      isEmpty={!hasData && !hasTotal}
                    />
                  </MetricRetry>
                );
              })}
            </Panel>
          </BodySection>
        </OverviewWrapper>
      </MetricPanelWrapper>
    </ErrorBoundary>
  );
};

export default MetricPanel;
